import React, { useMemo } from 'react';

import { ia } from 'lib/helpers/utility';
import { formatDate } from 'lib/helpers/dateUtils';
import { usePatientDashboardAppointments } from 'lib/hooks/queries/patients/usePatient';

import DocTable from 'components/shared/DocumentSheet/components/DocTable';
import DocSectionHead from 'components/shared/DocumentSheet/components/DocSectionHead';

const LIMIT = 4;

const COLUMNS = [
  { key: 'provider', label: 'Provider', align: 'left', strong: true },
  { key: 'when', label: 'Date & time', align: 'left', tabular: true },
  { key: 'type', label: 'Type', align: 'right' }
];

const formatAppointmentType = (location) => {
  const value = String(location || '').trim();

  if (!value) return '';

  const words = value.replace(/_/g, '-').toLowerCase();

  return words.charAt(0).toUpperCase() + words.slice(1);
};

const UpcomingTable = ({ patientId, practiceId, timezone }) => {
  const params = useMemo(
    () => ({
      patientId,
      practiceId,
      limit: LIMIT,
      withCount: true,
      withal: { practitioner: true },
      approved: true,
      upcoming: true
    }),
    [patientId, practiceId]
  );

  const { data } = usePatientDashboardAppointments({
    params,
    dependencies: [patientId, practiceId],
    options: {
      enabled: !!patientId,
      keepPreviousData: false
    }
  });

  const appointments = data?.appointments;

  const groups = useMemo(
    () => [
      {
        key: 'upcoming-appointments',
        rows: (appointments || []).map((appointment) => ({
          key: appointment.id,
          titleSuffix: appointment?.practitioner?.profession || '',
          cells: [
            {
              value:
                [appointment?.practitioner?.title, appointment?.practitioner?.fullName]
                  .filter(Boolean)
                  .join(' ')
                  .trim() || '-'
            },
            {
              value: [
                formatDate(appointment?.starts_at, timezone, 'MM/dd/yyyy'),
                formatDate(appointment?.starts_at, timezone, 'h:mm a')
              ]
                .filter(Boolean)
                .join(' · ')
            },
            { value: formatAppointmentType(appointment?.location), align: 'right' }
          ]
        }))
      }
    ],
    [appointments, timezone]
  );

  if (!ia(appointments)) return null;

  return (
    <div>
      <DocSectionHead title="Upcoming appointments" />
      <DocTable columns={COLUMNS} groups={groups} headerPadClass="pb-[5px]" />
    </div>
  );
};

export default UpcomingTable;
